import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../../context/contextAuth";

// Context Imports
import { useWeb } from "../../context/Dashboard/contextWeb";
import { useNetwork } from "../../context/Dashboard/contextNetwork";
import { useAwsUrl } from "../../context/Dashboard/aws/contextUrl";
import { useAwsIp } from "../../context/Dashboard/aws/contextIp";
import { useAwsConfig } from "../../context/Dashboard/aws/contextConfig";
import { useAzureUrl } from "../../context/Dashboard/azure/contextUrl";
import { useAzureIp } from "../../context/Dashboard/azure/contextIp";
import { useAzureConfig } from "../../context/Dashboard/azure/contextConfig";
import { useGcpUrl } from "../../context/Dashboard/gcp/contextUrl";
import { useGcpIp } from "../../context/Dashboard/gcp/contextIp";
import { useGcpConfig } from "../../context/Dashboard/gcp/contextConfig";
import { useDarkweb } from "../../context/Dashboard/contextDarkweb";

import { 
  scoreFromDb, 
  scoreFromMisconfigDb,
  calcBreachProbab 
} from "./t-scoring";

const useTScore = () => {
  const { signIn } = useAuth();
  const timeoutRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const [scores, setScores] = useState({
    web: { TV: 0, CV: 0, SS: 0, HV: 0, MV: 0, LV: 0 },
    network: { TV: 0, CV: 0, SS: 0, HV: 0, MV: 0, LV: 0 },
    cloud: { TV: 0, CV: 0, SS: 0, HV: 0, MV: 0, LV: 0 },
    misconfig: { critical: 0, warning: 0, clean: 0, total: 0, SS: 0 },
    identity: { risk: 0, scoreImpact: 0, SS: null, exposures: 0 },
    global: { TV: 0, HV: 0, MV: 0, LV: 0, CV: 0, SS: 0 },
    breachProbability: 0
  });

  // Pulling contexts
  const { webResults } = useWeb();
  const { networkResults } = useNetwork();
  const { webResults: awsS3 } = useAwsUrl();
  const { networkResults: awsEc2 } = useAwsIp();
  const { configAssets: awsMis } = useAwsConfig();
  const { webResults: azBlob } = useAzureUrl();
  const { networkResults: azVm } = useAzureIp();
  const { configAssets: azMis } = useAzureConfig();
  const { webResults: gcpGcs } = useGcpUrl();
  const { networkResults: gcpGce } = useGcpIp();
  const { configAssets: gcpMis } = useGcpConfig();
  const { intelligence: darkwebIntelligence } = useDarkweb();
  const darkwebOverview = darkwebIntelligence?.overview;

  const hasAssets = useMemo(() => (
    [webResults, networkResults, awsS3, awsEc2, azBlob, azVm, gcpGcs, gcpGce]
    .some(arr => (arr?.length ?? 0) > 0)
  ), [webResults, networkResults, awsS3, awsEc2, azBlob, azVm, gcpGcs, gcpGce]);

  const hasMisconfig = useMemo(() => (
    [awsMis, azMis, gcpMis].some(arr => (arr?.length ?? 0) > 0)
  ), [awsMis, azMis, gcpMis]);

  const initData = useCallback(async () => {
    if (!signIn) return;
    
    setLoading(true);
    try {
      // 1. Generate fresh scores from raw context data
      const w = scoreFromDb(webResults ?? []);
      const n = scoreFromDb(networkResults ?? []);
      const cloudRes = [awsS3, awsEc2, azBlob, azVm, gcpGcs, gcpGce].map(res => scoreFromDb(res ?? []));
      const misRes = [awsMis, azMis, gcpMis].map(res => scoreFromMisconfigDb(res ?? []));

      // 2. Aggregate Cloud Vulnerabilities
      const cloud = {
        TV: cloudRes.reduce((acc, curr) => acc + curr.TV, 0),
        CV: cloudRes.reduce((acc, curr) => acc + curr.CV, 0),
        HV: cloudRes.reduce((acc, curr) => acc + curr.HV, 0),
        MV: cloudRes.reduce((acc, curr) => acc + curr.MV, 0),
        LV: cloudRes.reduce((acc, curr) => acc + curr.LV, 0),
        SS: Math.round(cloudRes.reduce((acc, curr) => acc + curr.SS, 0) / (cloudRes.length || 1))
      };

      // 3. Aggregate Misconfigurations
      const critM = misRes.reduce((acc, curr) => acc + (curr.misconfigAll?.critical ?? 0), 0);
      const warnM = misRes.reduce((acc, curr) => acc + (curr.misconfigAll?.warning ?? 0), 0);
      const cleanM = misRes.reduce((acc, curr) => acc + (curr.misconfigAll?.clean ?? 0), 0);
      const misSS = Math.round(misRes.reduce((acc, curr) => acc + (curr.ss ?? 0), 0) / (misRes.length || 1));
      const totalM = critM + warnM + cleanM;

      // 4. Calculate Global Totals (Local scope - no stale state)
      const globalTV = w.TV + n.TV + cloud.TV;
      const globalCV = w.CV + n.CV + cloud.CV;
      const globalHV = w.HV + n.HV + cloud.HV;
      const globalMV = w.MV + n.MV + cloud.MV;
      const globalLV = w.LV + n.LV + cloud.LV;
      const baseGlobalSS = Math.round(
        (w.SS + n.SS + cloud.SS + misSS) / 4
      );
      const identityPenalty = darkwebOverview?.has_assessment
        ? Math.abs(Number(darkwebOverview.save_score_impact) || 0)
        : 0;
      const globalSS = Math.max(
        0,
        Math.round((baseGlobalSS - identityPenalty) * 10) / 10
      );

      // 5. CRITICAL FIX: Use the calculated locals for Probability
      const { probability } = calcBreachProbab(
        globalTV,
        globalCV,
        totalM,
        critM
      );

      // 6. Final State Update
      setScores({
        web: w,
        network: n,
        cloud,
        misconfig: { critical: critM, warning: warnM, clean: cleanM, total: totalM, SS: misSS },
        identity: {
          risk: Number(darkwebOverview?.risk_score) || 0,
          scoreImpact: -identityPenalty,
          SS: darkwebOverview?.has_assessment
            ? Number(darkwebOverview.identity_score)
            : null,
          exposures: Number(darkwebOverview?.requires_attention) || 0,
        },
        global: { TV: globalTV, HV: globalHV, MV: globalMV, LV: globalLV, CV: globalCV, SS: globalSS },
        breachProbability: probability
      });
    } catch (err) {
      console.error("T-Scoring Engine Failure:", err);
    } finally {
      setLoading(false);
    }
  }, [signIn, webResults, networkResults, awsS3, awsEc2, azBlob, azVm, gcpGcs, gcpGce, awsMis, azMis, gcpMis, darkwebOverview]);

  useEffect(() => {
    if (signIn && (hasAssets || hasMisconfig)) {
      initData();
    }
  }, [signIn, hasAssets, hasMisconfig, initData]);

  // UI Helper: Memoized Chart formatting
  const chartData = useMemo(() => {
    const palette = { crit: "#6d2d9d", high: "#fc0000", med: "#ffc000", low: "#92d14f" };
    const format = (s) => [
      { name: "Low", value: s.LV, fill: palette.low },
      { name: "Medium", value: s.MV, fill: palette.med },
      { name: "High", value: s.HV, fill: palette.high },
      { name: "Critical", value: s.CV, fill: palette.crit },
    ];

    return {
      web: format(scores.web),
      network: format(scores.network),
      cloud: format(scores.cloud),
      misconfig: [
        { name: "Clean", value: scores.misconfig.clean, fill: palette.low },
        { name: "Warning", value: scores.misconfig.warning, fill: palette.med },
        { name: "Critical", value: scores.misconfig.critical, fill: palette.crit },
      ],
      pie: [
        { name: "Critical", value: scores.global.CV, fill: palette.crit },
        { name: "High", value: scores.global.HV, fill: palette.high },
        { name: "Medium", value: scores.global.MV, fill: palette.med },
        { name: "Low", value: scores.global.LV, fill: palette.low },
      ]
    };
  }, [scores]);

  return {
    loading,
    TVulScore: scores.global.TV,
    THVulScore: scores.global.HV,
    TMVulScore: scores.global.MV,
    TLVulScore: scores.global.LV,
    TCVulScore: scores.global.CV,
    TSaveScore: scores.global.SS,
    // Individual Scores for Cards
    webTVulScore: scores.web.TV,
    webCVulScore: scores.web.CV,
    webSaveScore: scores.web.SS,
    networkTVulScore: scores.network.TV,
    networkCVulScore: scores.network.CV,
    networkSaveScore: scores.network.SS,
    cloudTVulScore: scores.cloud.TV,
    cloudCVulScore: scores.cloud.CV,
    cloudSaveScore: scores.cloud.SS,
    cloudCriticalScore: scores.misconfig.critical,
    cloudWarningScore: scores.misconfig.warning,
    cloudCleanScore: scores.misconfig.clean,
    cloudMisconfigAll: scores.misconfig.total,
    cloudMisconfigScore: scores.misconfig.SS,
    identityExposureRisk: scores.identity.risk,
    identityExposureScore: scores.identity.SS,
    identityScoreImpact: scores.identity.scoreImpact,
    identityExposures: scores.identity.exposures,
    // Probability & Charts
    breachProbability: scores.breachProbability,
    pieData: chartData.pie,
    webData: chartData.web,
    networkData: chartData.network,
    cloudData: chartData.cloud,
    cloudMisconfigData: chartData.misconfig,
    hasAssets,
    hasMisconfig,
    hasScore: scores.global.TV > 0,
    initData,
    setLoading,
    timeoutRef,
  };
};

export default useTScore;
